import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ShieldCheck, Trash, EnvelopeSimple, Plus } from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock } from './module-ui';

import { BACKEND_URL, apiPath } from '../config/apiBase';
import { notifySuccess, notifyError } from '../lib/notify';
const API = apiPath('/api/access-control');

function authHeaders() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

export default function AccessControlPage() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [domainInput, setDomainInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/rules`, authHeaders());
      setRules(r.data || []);
    } catch (e) { setError(e.response?.data?.detail || 'Failed to load rules'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const flash = (m) => { notifySuccess(m); setError(null); };

  const domains = rules.filter((r) => r.type === 'domain');
  const emails = rules.filter((r) => r.type === 'email');
  const noRules = rules.filter((r) => r.active).length === 0;

  const addRule = async (type, value, extra = {}) => {
    if (!value.trim()) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/rules`, { type, value: value.trim(), ...extra }, authHeaders());
      flash(type === 'email' && extra.send_invite ? 'Email whitelisted — invite sent' : `${type === 'domain' ? 'Domain' : 'Email'} added`);
      setDomainInput(''); setEmailInput('');
      await load();
    } catch (e) { setError(e.response?.data?.detail || 'Failed to add rule'); }
    finally { setBusy(false); }
  };

  const toggle = async (rule) => {
    try { await axios.patch(`${API}/rules/${rule.rule_id}`, { active: !rule.active }, authHeaders()); await load(); }
    catch (e) { setError(e.response?.data?.detail || 'Update failed'); }
  };
  const remove = async (rule) => {
    if (!window.confirm(`Remove ${rule.value}?`)) return;
    try { await axios.delete(`${API}/rules/${rule.rule_id}`, authHeaders()); flash('Removed'); await load(); }
    catch (e) { setError(e.response?.data?.detail || 'Delete failed'); }
  };
  const resendInvite = async (rule) => {
    try { await axios.post(`${API}/rules/${rule.rule_id}/resend-invite`, {}, authHeaders()); flash(`Invite re-sent to ${rule.value}`); }
    catch (e) { setError(e.response?.data?.detail || 'Resend failed'); }
  };

  return (
    <ModuleShell
      title="Access Control"
      icon={ShieldCheck}
      subtitle="Control who can sign in. Allow whole email domains, or whitelist individual emails. Anyone outside the list is denied at login and Google sign-in."
    >
    <div style={{ maxWidth: '900px' }}>
      {error && <Banner>{typeof error === 'string' ? error : JSON.stringify(error)}</Banner>}
      {noRules && (
        <Banner warn>
          No active rules yet — <strong>everyone can currently sign in</strong>. Add a domain
          (e.g. <code>kapilit.com</code>) to lock access down to your organisation.
        </Banner>
      )}

      {loading ? <SkeletonBlock rows={4} /> : (
        <div style={{ display: 'grid', gap: '24px' }}>
          {/* Domains */}
          <section style={card}>
            <h2 style={h2}>Allowed domains</h2>
            <p style={hint}>Any email at these domains can sign in (Google or password).</p>
            <div style={{ display: 'flex', gap: '8px', margin: '12px 0' }}>
              <input
                style={input}
                placeholder="kapilit.com"
                value={domainInput}
                onChange={(e) => setDomainInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addRule('domain', domainInput)}
              />
              <button style={primaryBtn} disabled={busy} onClick={() => addRule('domain', domainInput)}>
                <Plus size={16} weight="bold" /> Add domain
              </button>
            </div>
            <RuleList rules={domains} onToggle={toggle} onRemove={remove} emptyText="No domains yet." />
          </section>

          {/* Emails */}
          <section style={card}>
            <h2 style={h2}>Whitelisted emails</h2>
            <p style={hint}>
              Individual emails allowed regardless of domain. When added with an invite, the
              person gets a link to set a password and can then log in with email + password.
            </p>
            <div style={{ display: 'flex', gap: '8px', margin: '12px 0', flexWrap: 'wrap' }}>
              <input
                style={{ ...input, minWidth: '240px' }}
                placeholder="person@gmail.com"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addRule('email', emailInput, { send_invite: sendInvite })}
              />
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                <input type="checkbox" checked={sendInvite} onChange={(e) => setSendInvite(e.target.checked)} />
                Send set-password invite
              </label>
              <button style={primaryBtn} disabled={busy} onClick={() => addRule('email', emailInput, { send_invite: sendInvite })}>
                <Plus size={16} weight="bold" /> Add email
              </button>
            </div>
            <RuleList rules={emails} onToggle={toggle} onRemove={remove} onResend={resendInvite} emptyText="No whitelisted emails yet." />
          </section>
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function RuleList({ rules, onToggle, onRemove, onResend, emptyText }) {
  if (!rules.length) return <p style={{ color: '#9CA3AF', fontSize: '12px', margin: 0 }}>{emptyText}</p>;
  return (
    <div style={{ display: 'grid', gap: '8px' }}>
      {rules.map((r) => (
        <div key={r.rule_id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 14px', border: '1px solid #E5E7EB', borderRadius: '8px',
          background: r.active ? 'white' : '#F9FAFB', opacity: r.active ? 1 : 0.7,
        }}>
          <div>
            <span style={{ fontWeight: 600 }}>{r.value}</span>
            {!r.active && <span style={pill}>disabled</span>}
            {r.note && <span style={{ color: '#6B7280', fontSize: '12px', marginLeft: '8px' }}>· {r.note}</span>}
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {onResend && (
              <button style={linkBtn} onClick={() => onResend(r)} title="Resend set-password invite">
                <EnvelopeSimple size={16} /> Resend invite
              </button>
            )}
            <button style={linkBtn} onClick={() => onToggle(r)}>{r.active ? 'Disable' : 'Enable'}</button>
            <button style={{ ...linkBtn, color: '#DC2626' }} onClick={() => onRemove(r)}><Trash size={16} /></button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Banner({ children, ok, warn }) {
  const bg = ok ? '#D1FAE5' : warn ? '#FEF3C7' : '#FEE2E2';
  const fg = ok ? '#065F46' : warn ? '#92400E' : '#B91C1C';
  return <div style={{ background: bg, color: fg, padding: '6px 10px', borderRadius: '8px', marginBottom: '8px', fontSize: '12px' }}>{children}</div>;
}

const card = { background: 'white', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '10px' };
const h2 = { fontSize: '17px', fontWeight: 600, margin: '0 0 2px' };
const hint = { color: '#6B7280', fontSize: '12px', margin: 0 };
const input = { flex: 1, padding: '6px 8px', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '12px' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 16px', background: '#002FA7', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer' };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'none', border: 'none', color: '#002FA7', cursor: 'pointer', fontSize: '12px', fontWeight: 500 };
const pill = { marginLeft: '8px', fontSize: '11px', background: '#FEE2E2', color: '#B91C1C', padding: '2px 8px', borderRadius: '999px' };
